// Focus back on the row that opened a drawer, once no drawer is open (finance-real walk, 2026-10-23). Radix's modal
// Dialog.Content cancels its own focus return and focuses the dialog's Trigger instead, and the shared Drawer has none:
// every finance drawer opens from the URL. So without this, closing one leaves focus on the page's body. What is kept is
// the focusable row the focused element sits in, not the element: a status pick or a row's ⋮ item leaves focus on its menu's
// trigger (DropdownMenu), which a status change can unmount with its cell. With no opener still on the page (a drawer a
// link or a reload opened, a row whose status moved it to another tab, a create button with nothing left to create),
// focus goes to the first of `fallback`'s elements that takes it: the page's first row, its create button, then its tab
// (the S7 review, the S8 walk). A hidden one (a phone's strip) takes no focus, so the next one gets it. It moves focus
// only off the body: a close by a click elsewhere (a tab) leaves focus where that click put it. Visual state only, kept
// in refs.
import { useCallback, useEffect, useRef } from "react";

/** Focuses the first candidate that takes focus. */
function focusFirst(candidates: (HTMLElement | null | undefined)[]): void {
  for (const el of candidates) {
    if (!el?.isConnected) continue;
    el.focus();
    if (document.activeElement === el) return;
  }
}

/** Focus sits nowhere: on the body, or on an element that left the page. */
const focusLost = (): boolean => {
  const active = document.activeElement;
  return active === null || active === document.body || !active.isConnected;
};

/** Call the returned function as a row opens a drawer: the focused element then is in the row (a click or Enter focuses
 * it, and a menu's pick its trigger). */
export function useOpenerFocus(modal: string | null, fallback?: () => (HTMLElement | null | undefined)[]): () => void {
  const opener = useRef<HTMLElement | null>(null);
  const was = useRef<string | null>(null);
  const fallbackRef = useRef(fallback);
  fallbackRef.current = fallback;
  useEffect(() => {
    const closed = was.current !== null && modal === null;
    was.current = modal;
    if (!closed) return;
    const row = opener.current;
    opener.current = null;
    // After Radix's own close step, queued as the drawer unmounted in this same commit, which focuses nothing here.
    const timer = window.setTimeout(() => {
      if (!focusLost()) return;
      focusFirst([row, ...(fallbackRef.current?.() ?? [])]);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [modal]);
  return useCallback(() => {
    const focused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // Only a row that takes focus: a plain one would swallow it.
    opener.current = focused?.closest<HTMLElement>("tr[tabindex]") ?? focused;
  }, []);
}
